import type { RouteRecordRaw } from 'vue-router'

/**
 * -----------------------------------------------------------------------------
 * Shell Route Definitions
 * -----------------------------------------------------------------------------
 * This file defines all shell-owned routes in the application.
 *
 * Responsibilities:
 * - Host MFE catch-all routes (Accounts, Commerce, Service)
 * - Render shell-only dashboards
 * - Provide global error & fallback pages
 *
 * NOTE:
 * - MFE routes are intentionally defined as catch-all placeholders.
 * - Access control and redirection are handled dynamically at runtime.
 *
 * @author Harvinder Singh
 * @created 2026
 * -----------------------------------------------------------------------------
 */

/**
 * Shared meta configuration for pages where
 * global search should be hidden.
 */
const hideSearchMeta = {
  search: {
    show: false,
  },
}

/**
 * -----------------------------------------------------------------------------
 * Base Route Definitions
 * -----------------------------------------------------------------------------
 */
const routes: RouteRecordRaw[] = [
  // ===========================================================================
  // Reserved MFE Host Routes (Catch-All per Microfrontend)
  // ===========================================================================
  // These routes act as mounting points for MFEs.
  // The shell loads a generic MFE loader component,
  // which then bootstraps the remote application.
  {
    path: '/accounts/:pathMatch(.*)*',
    name: 'AccountsMFE',
    component: () => import('@/modules/Mfa/MFALoadingPage.vue'),
  },
  {
    path: '/commerce/:pathMatch(.*)*',
    name: 'CommerceMFE',
    component: () => import('@/modules/Mfa/MFALoadingPage.vue'),
  },
  {
    path: '/service/:pathMatch(.*)*',
    name: 'ServiceMFE',
    component: () => import('@/modules/Mfa/MFALoadingPage.vue'),
  },

  // ===========================================================================
  // Shell-Hosted Dashboards (Non-MFE Views)
  // ===========================================================================
  // These routes are fully owned by the shell and do not load MFEs.
  // They typically serve high-level dashboards or landing pages.
  {
    path: '/merchandising/dashboard',
    name: 'MhcDashboard',
    component: () => import('@/modules/Dashboards/MhcDashboardPage.vue'),
    meta: {
      ...hideSearchMeta,
    },
  },
  {
    path: '/neto/dashboard',
    name: 'NetoDashboard',
    component: () => import('@/modules/Dashboards/NetoDashboardPage.vue'),
    meta: {
      ...hideSearchMeta,
    },
  },

  // ===========================================================================
  // Authentication
  // ===========================================================================
  {
    path: '/authentication',
    name: 'Authentication',
    component: () => import('@/modules/ErrorPages/AuthenticationPage.vue'),
  },
  {
    path: '/select-account',
    name: 'SelectAccount',
    meta: { requireAuth: true, layout: 'default' },
    component: () => import('@/lib/components/SelectAccountPage.vue')
  }, 

  // ===========================================================================
  // Access & Permission Error Pages
  // ===========================================================================
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/modules/ErrorPages/AppAccessDeniedPage.vue'),
    meta: hideSearchMeta,
  },
  {
    path: '/preview/:productName',
    name: 'NoProductAccess',
    component: () => import('@/modules/CTAPages/ProductLandingPage.vue'),
    meta: hideSearchMeta,
  },
  {
    path: '/coming-soon/:productName',
    name: 'coming-soon',
    redirect: (to) => ({
      name: 'NoProductAccess',
      params: to.params,
      query: to.query
    }),
  },

  // ===========================================================================
  // Application Unavailable
  // ===========================================================================
  // Displayed when an MFE or backend service is temporarily unavailable.
  {
    path: '/unavailable/:appName',
    name: 'AppUnavailable',
    component: () => import('@/modules/ErrorPages/AppUnavailablePage.vue'),
    props: true,
    meta: hideSearchMeta,
  },

  // ===========================================================================
  // Shell-Level 404 (Must Always Be Last)
  // ===========================================================================
  // This fallback ensures that unknown routes do not leak into MFEs
  // and are handled consistently by the shell.
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/modules/ErrorPages/NotFoundPage.vue'),
    meta: {
      ...hideSearchMeta,
      description: 'Fallback 404 page for unknown routes',
    },
  },
]

export default routes